// @ts-check

/** @file Opt-in frontend performance diagnostics. */

import Logger from "velocious/build/src/logger.js"

const performanceLogger = new Logger("AwesomeTasksPerformance", {
  debug: true,
  loggingConfiguration: {
    console: true,
    levels: ["info"]
  }
})
let cachedSearch = ""
let cachedEnabled = false

/**
 * Checks whether frontend performance diagnostics are enabled.
 * @returns {boolean} True when performance logging is enabled.
 */
export function performanceDebugEnabled() {
  if (
    typeof process !== "undefined" &&
    process.env?.EXPO_PUBLIC_PERFORMANCE_DEBUG === "true"
  ) {
    return true
  }

  if (typeof window === "undefined" || !window.location) return false

  const search = window.location.search
  if (search === cachedSearch) return cachedEnabled

  cachedSearch = search
  cachedEnabled = new URLSearchParams(search).get("performanceDebug") === "true"

  return cachedEnabled
}

/**
 * Reads a monotonic timestamp where available.
 * @returns {number} Timestamp in milliseconds.
 */
export function performanceNow() {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now()
  }

  return Date.now()
}

/**
 * Logs a frontend performance event when diagnostics are enabled.
 * @param {string} label Event label.
 * @param {Record<string, unknown>} [details] Structured event details.
 * @returns {void} Nothing.
 */
export function performanceLog(label, details = {}) {
  if (!performanceDebugEnabled()) return

  void performanceLogger.info(label, details)
}

/**
 * Logs elapsed milliseconds for an operation when diagnostics are enabled.
 * @param {string} label Event label.
 * @param {number} startedAt Start timestamp from `performanceNow()`.
 * @param {Record<string, unknown>} [details] Structured event details.
 * @returns {void} Nothing.
 */
export function performanceMeasure(label, startedAt, details = {}) {
  if (!performanceDebugEnabled()) return

  performanceLog(label, {
    ...details,
    durationMs: Math.round((performanceNow() - startedAt) * 10) / 10
  })
}
